import { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import PastoralProgress from "../components/PastoralProgress";
import PageMeta from "../components/PageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Structured-hint chips — optional breakthroughs a recipient can flag.
// These seed the classifier so the progress prompt gets a cleaner read
// even when the free-text notes are brief.
const FORGIVEN_OPTIONS = ["father", "mother", "sibling", "ex partner", "religious leader", "friend"];
const RELEASED_REFUGES = ["bitterness", "anger", "self-reliance", "people-pleasing", "isolation", "worry", "addiction"];
const HEALED_CATEGORIES = ["father-wound", "mother-wound", "shame", "rejection", "fear", "grief", "false refuge"];

function toggleIn(list, value) {
  return list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
}

export default function Progress() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [forgiven, setForgiven] = useState([]);
  const [released, setReleased] = useState([]);
  const [healed, setHealed] = useState([]);
  const [phase, setPhase] = useState("");
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);

  const canSubmit = !working && file && notes.trim().length >= 10;

  async function onSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setWorking(true);
    setPhase("Reading your last packet…");
    try {
      const fd = new FormData();
      fd.append("pdf", file);
      fd.append("progress_notes", notes.trim());
      fd.append("forgiven_persons", forgiven.join(", "));
      fd.append("released_refuges", released.join(", "));
      fd.append("healed_categories", healed.join(", "));
      const { data } = await axios.post(`${API}/progress/start`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const jobId = data.job_id;
      setPhase("Reading where you are on the ladder…");
      const started = Date.now();
      // Poll every 2s up to ~3 min.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        const res = await axios.get(`${API}/progress/job/${jobId}`);
        if (res.data.status === "ready" && res.data.packet_id) {
          navigate(`/progress/${res.data.packet_id}`);
          return;
        }
        if (res.data.status === "error") {
          throw new Error(res.data.error || "The Matrix stumbled. Please try again.");
        }
        if (Date.now() - started > 180000) {
          throw new Error("This is taking longer than expected. Please try again.");
        }
        if (res.data.phase) setPhase(res.data.phase);
      }
    } catch (e2) {
      setError(e2?.response?.data?.detail || e2.message || "Something went wrong.");
      setWorking(false);
      setPhase("");
    }
  }

  return (
    <main className="min-h-screen rm-cinematic text-[#F0E6D2] px-6 sm:px-12 md:px-20 py-12" data-testid="progress-page">
      <PageMeta
        title="Ladder Progress"
        description="Upload your prior Restoration Matrix packet and receive a fresh reading of Jacob's Ladder — showing you the rung you're standing on today and what the next faithful step is."
        path="/progress"
      />
      <header className="mb-12 max-w-3xl mx-auto text-center">
        <Link to="/" className="overline text-[#FFE7A0]/70 hover:text-[#FFE7A0]" data-testid="progress-back-link">
          ← The Restoration Matrix
        </Link>
        <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl text-[#FFE7A0] leading-tight" data-testid="progress-title">
          Ladder Progress
        </h1>
        <p className="mt-5 text-[#F0E6D2]/80 font-body font-light leading-relaxed text-base sm:text-lg" data-testid="progress-blurb">
          Upload your original Restoration Matrix packet, tell us what you&rsquo;ve
          walked through, and we&rsquo;ll read the ladder again — showing you the
          rung you&rsquo;re standing on now and what&rsquo;s next.
        </p>
        <div className="mt-6 flex justify-center">
          <span className="rm-privacy-inline" data-testid="progress-privacy-disclaimer">
            <span className="rm-privacy-dot" aria-hidden="true" />
            Privacy guaranteed — no record of anything is saved (your packet and inputs included), no account needed.
          </span>
        </div>
      </header>

      <form className="max-w-3xl mx-auto space-y-12" onSubmit={onSubmit} data-testid="progress-form">
        {/* 1. Upload PDF */}
        <section>
          <label className="overline block mb-3 text-[0.65rem]" htmlFor="progress-pdf">
            Your original Matrix packet (PDF)
          </label>
          <div
            className="border border-[#FFE7A0]/40 bg-[#FFE7A0]/[0.04] px-5 py-5 cursor-pointer hover:bg-[#FFE7A0]/[0.08] transition-colors"
            onClick={() => fileRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileRef.current?.click(); }}
            data-testid="progress-upload-target"
          >
            <input
              ref={fileRef}
              type="file"
              id="progress-pdf"
              accept="application/pdf"
              className="hidden"
              data-testid="progress-pdf-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <div className="font-serif-display text-lg sm:text-xl text-[#FFE7A0]" data-testid="progress-upload-label">
              {file ? file.name : "Tap to choose your packet PDF"}
            </div>
            <div className="mt-1 text-xs text-[#F0E6D2]/55 italic">
              {file ? `${(file.size / 1024).toFixed(0)} KB · ready` : "Must be a Restoration Matrix PDF you received earlier."}
            </div>
          </div>
        </section>

        {/* 2. Free-text progress notes */}
        <section>
          <label className="overline block mb-3 text-[0.65rem]" htmlFor="progress-notes">
            What have you walked through since your last packet?
          </label>
          <textarea
            id="progress-notes"
            data-testid="progress-notes-input"
            className="rm-input rm-textarea"
            rows={8}
            placeholder="Write in your own words. Be specific where you can — who you forgave, what you released, what healed, what&rsquo;s still tender. There are no right words."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className="mt-2 text-xs text-[#F0E6D2]/45 italic">
            Minimum a sentence or two. Pour out what God has done.
          </div>
        </section>

        {/* 3. Structured hints (optional) */}
        <section data-testid="progress-hints">
          <div className="overline mb-4 text-[0.65rem]">Optional breakthroughs — tap any that apply</div>

          <HintRow label="Who have you forgiven?" options={FORGIVEN_OPTIONS}
                   selected={forgiven} onToggle={(v) => setForgiven(toggleIn(forgiven, v))}
                   testIdPrefix="progress-forgiven" />
          <HintRow label="What false refuges have you released?" options={RELEASED_REFUGES}
                   selected={released} onToggle={(v) => setReleased(toggleIn(released, v))}
                   testIdPrefix="progress-released" />
          <HintRow label="Where have you felt healing land?" options={HEALED_CATEGORIES}
                   selected={healed} onToggle={(v) => setHealed(toggleIn(healed, v))}
                   testIdPrefix="progress-healed" />
        </section>

        {/* 4. Submit */}
        <section className="pt-4 text-center">
          {error && (
            <div className="mb-6 text-sm text-red-300 font-body" data-testid="progress-error">{error}</div>
          )}
          {working ? (
            <ProgressWorking phase={phase} />
          ) : (
            <motion.button
              type="submit"
              disabled={!canSubmit}
              whileHover={canSubmit ? { scale: 1.02 } : {}}
              whileTap={canSubmit ? { scale: 0.98 } : {}}
              className={`rm-btn-solid ${canSubmit ? "" : "opacity-40 cursor-not-allowed"}`}
              data-testid="progress-submit-btn"
            >
              Read my new ladder position
            </motion.button>
          )}
          <p className="mt-5 text-xs text-[#F0E6D2]/50 italic max-w-md mx-auto">
            This takes about 40 seconds and costs about as much as a coffee shop tip.
          </p>
        </section>
      </form>
    </main>
  );
}

function HintRow({ label, options, selected, onToggle, testIdPrefix }) {
  return (
    <div className="mb-6" data-testid={`${testIdPrefix}-row`}>
      <div className="text-sm font-body font-light text-[#F0E6D2]/80 mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              data-testid={`${testIdPrefix}-chip-${opt.replace(/[^a-z0-9]+/gi, "-")}`}
              onClick={() => onToggle(opt)}
              className={`px-3 py-1.5 text-xs font-body tracking-wide border transition-colors ${
                active
                  ? "bg-[#FFE7A0] text-[#0a0f24] border-[#FFE7A0]"
                  : "bg-transparent text-[#F0E6D2]/80 border-[#FFE7A0]/30 hover:border-[#FFE7A0]/70"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ProgressWorking({ phase }) {
  return (
    <div data-testid="progress-working" className="w-full">
      <PastoralProgress
        phase={phase || "Reading the ladder…"}
        testIdPrefix="progress-pastoral"
      />
    </div>
  );
}
